import PropTypes from 'prop-types';
import { BsFacebook, BsInfoCircle, BsInstagram, BsLinkedin } from 'react-icons/bs';

import SectionCard from '#globalComponents/Card/SectionCard';

import { LEGAL_FORM_OPTIONS } from '../LeadOverviewPage/searchFilters/components/filterGroups/shared';

const LEGAL_FORM_LABELS = Object.fromEntries(LEGAL_FORM_OPTIONS.map(({ value, label }) => [value, label]));

const STATUS_LABELS = {
	active: 'Aktiv',
	inactive: 'Inaktiv',
	liquidation: 'In Liquidation',
};

const SOCIAL_LINKS = [
	{ key: 'linkedinUrl', label: 'LinkedIn', icon: BsLinkedin },
	{ key: 'instagramUrl', label: 'Instagram', icon: BsInstagram },
	{ key: 'facebookUrl', label: 'Facebook', icon: BsFacebook },
];

const DataRow = ({ label, value, href }) => {
	if (!value) return null;
	return (
		<div className="flex items-start gap-4 border-b border-bgBase-500 py-2.5 last:border-0">
			<dt className="w-32 flex-shrink-0 text-sm text-bgBase-800">{label}</dt>
			<dd className="min-w-0 text-sm font-medium text-text">
				{href ? (
					<a
						href={href}
						target="_blank"
						rel="noreferrer"
						className="block truncate text-primary-600 hover:underline"
					>
						{value}
					</a>
				) : (
					value
				)}
			</dd>
		</div>
	);
};

DataRow.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.string,
	href: PropTypes.string,
};

const CompanyMasterData = ({ company }) => {
	const address = [company.addressStreet, company.addressPostal, company.addressCity].filter(Boolean).join(', ');
	const foundingDate = company.foundingDate ? new Date(company.foundingDate).toLocaleDateString('de-DE') : null;
	const register = [company.registerAuthority, company.registerNumber].filter(Boolean).join(' ');
	const socialLinks = SOCIAL_LINKS.filter(({ key }) => company[key]);

	return (
		<SectionCard
			title="Stammdaten"
			icon={BsInfoCircle}
			bodyClassName="py-1.5"
			headerAction={
				socialLinks.length > 0 && (
					<div className="flex items-center gap-1">
						{socialLinks.map(({ key, label, icon: Icon }) => (
							<a
								key={key}
								href={company[key]}
								target="_blank"
								rel="noreferrer"
								title={label}
								aria-label={label}
								className="inline-flex h-7 w-7 items-center justify-center rounded-control text-bgBase-800 transition-colors hover:bg-primary-100 hover:text-primary-600"
							>
								<Icon className="h-4 w-4" />
							</a>
						))}
					</div>
				)
			}
		>
			<dl>
				<DataRow label="Name" value={company.companyName} />
				<DataRow label="Rechtsform" value={LEGAL_FORM_LABELS[company.legalForm] || company.legalForm} />
				<DataRow label="Adresse" value={address} />
				<DataRow label="Telefon" value={company.phoneNumber} />
				<DataRow label="Mobil" value={company.mobilePhoneNumber} />
				<DataRow label="E-Mail" value={company.emailAddress} href={`mailto:${company.emailAddress}`} />
				<DataRow
					label="Website"
					value={company.websiteUrl?.replace(/^https?:\/\/(www\.)?/, '')}
					href={company.websiteUrl}
				/>
				<DataRow label="Gründung" value={foundingDate} />
				<DataRow label="Branche" value={company.companyCategory?.join(', ')} />
				<DataRow label="WZ-Code" value={company.industryCodes?.join(', ')} />
				<DataRow label="Registergericht" value={register} />
				{register && (
					<DataRow label="Status" value={STATUS_LABELS[company.companyStatus] || company.companyStatus} />
				)}
			</dl>
		</SectionCard>
	);
};

CompanyMasterData.propTypes = {
	company: PropTypes.object.isRequired,
};

export default CompanyMasterData;
